import { useEffect, useState } from "react";
import { Clock, X, RotateCcw, Trash2, Loader2, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export type DraftKind = "social" | "email";

/** One saved generation. `output` shape varies by kind:
 *   - social → Record<platform, string[]>
 *   - email  → { subjectLine; previewText; body }[]
 *  Parents narrow `output` themselves on load. */
export interface HistoryDraft {
  id: string;
  kind: DraftKind;
  topic: string;
  tone: string;
  inputs: unknown;
  output: unknown;
  imageUrl: string | null;
  createdAt: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Short meta line under each row — platforms (social) or audience (email). */
function metaFor(d: HistoryDraft): string {
  const inputs = (d.inputs ?? {}) as Record<string, unknown>;
  if (d.kind === "social") {
    const platforms = Array.isArray(inputs.platforms)
      ? (inputs.platforms as string[])
      : [];
    const names = platforms.length ? platforms.join(", ") : "social";
    return `${names} · ${d.tone}`;
  }
  const listName =
    typeof inputs.listName === "string" ? inputs.listName : "All customers";
  return `${listName} · ${d.tone}`;
}

// ─── Drawer ─────────────────────────────────────────────────────────────────────

export function HistoryDrawer({
  kind,
  open,
  onClose,
  onLoad,
}: {
  kind: DraftKind;
  open: boolean;
  onClose: () => void;
  onLoad: (draft: HistoryDraft) => void;
}) {
  const [drafts, setDrafts] = useState<HistoryDraft[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<{ drafts?: HistoryDraft[] }>(`/api/marketing/history?kind=${kind}`)
      .then((data) => {
        if (!cancelled) setDrafts(data.drafts ?? []);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, kind]);

  const remove = async (id: string) => {
    setDeletingId(id);
    try {
      await api(`/api/marketing/history/${id}`, { method: "DELETE" });
      setDrafts((prev) => prev.filter((d) => d.id !== id));
    } catch {
      // Leave the row in place — the user can retry.
    } finally {
      setDeletingId(null);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-30 bg-brown-900/30 backdrop-blur-[2px]"
        onClick={onClose}
      />
      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-brown-100 px-6 py-5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-peach-100">
              <Clock className="h-4 w-4 text-brown-500" />
            </div>
            <div>
              <h2 className="font-display font-bold text-brown-500">History</h2>
              <p className="text-xs text-brown-400">
                Your recent {kind === "social" ? "post" : "email"} drafts
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-brown-300 hover:bg-cream-100 hover:text-brown-500"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-peach-400" />
            </div>
          )}

          {!loading && error && (
            <p className="px-2 py-6 text-sm text-rust-600">
              Couldn't load history: {error}
            </p>
          )}

          {!loading && !error && drafts.length === 0 && (
            <div className="px-2 py-16 text-center">
              <Clock className="mx-auto h-8 w-8 text-muted" />
              <p className="mt-3 text-sm font-semibold text-brown-500">
                No saved drafts yet
              </p>
              <p className="mt-1 text-sm text-brown-400">
                Drafts you generate are saved here automatically.
              </p>
            </div>
          )}

          {!loading && !error && drafts.length > 0 && (
            <ul className="space-y-2">
              {drafts.map((d) => (
                <li
                  key={d.id}
                  className="group rounded-lg border border-brown-100 bg-white p-4 hover:border-brown-200 hover:bg-cream-50/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="line-clamp-2 text-sm font-medium text-brown-500">
                      {d.topic}
                    </p>
                    {d.imageUrl && (
                      <ImageIcon
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink underline underline-offset-4"
                        aria-label="Has generated image"
                      />
                    )}
                  </div>
                  <p className="mt-1 text-xs capitalize text-brown-400">
                    {metaFor(d)}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[11px] text-brown-300">
                      {fmtWhen(d.createdAt)}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          onLoad(d);
                          onClose();
                        }}
                        className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-brown-400 hover:bg-peach-50 hover:text-brown-500"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Load
                      </button>
                      <button
                        onClick={() => remove(d.id)}
                        disabled={deletingId === d.id}
                        className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-brown-300 hover:bg-rust-50 hover:text-rust-600 disabled:opacity-50"
                      >
                        {deletingId === d.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
